import {
	Component,
	ChangeDetectionStrategy,
	inject,
	Injector,
	input,
	output,
	signal,
	computed,
	effect,
	afterNextRender,
	OnDestroy,
	PLATFORM_ID,
	LOCALE_ID,
	viewChild,
	viewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	DateRange,
	MatCalendar,
	MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import {
	CdkOverlayOrigin,
	ConnectedPosition,
	OverlayModule,
} from '@angular/cdk/overlay';
import { FilterService } from '@framework/analytics/services/filter.service';
import {
	ThemeService,
	ThemeMode,
} from '@framework/analytics/services/theme.service';
import { ThemeConfig } from '@framework/analytics/models/theme.model';
import type { DateRangeExpression } from '@framework/analytics/models/date-range-expression.model';
import type { DateRangeUnit } from '@framework/analytics/models/date-range-config.model';
import {
	resolveDateRangeExpression,
	tryResolveDateRangeExpression,
	toMaterialDateRangeFromExpression,
	expressionFromResolvedDates,
	expressionFromDynamicRelativeForm,
	expressionFromFixedAndEnd,
	formatDateToISO,
	parseISODate,
	buildRelativeAgoToken,
	parseRelativeAgoToken,
	isIsoDateToken,
	isValidDateRangeExpression,
	parseDateRangeExpressionInput,
	labelForDateRangeExpression,
	expressionDependsOnWeekStart,
	isDynamicRangeExpression,
	ALL_PRESETS,
	DEFAULT_PRESETS,
	DEFAULT_WEEK_START,
	DEFAULT_DATE_RANGE_DISPLAY_FORMAT,
	coerceWeekStart,
	formatDateRangeDisplayDate,
	getFirstDayOfWeekIndex,
	type DateRangePresetOption,
	type WeekStartSetting,
	type WeekStart,
} from '@framework/analytics/utils/date-range.util';
import { JeptoDateRangePickerPanelDirective } from './week-start-date-adapter';
import {
	applyDualPanelCalendarA11y,
	buildDualPanelCalendarHighlightStyles,
} from './jepto-date-range-picker-dual-panel-calendar.styles';
import {
	JeptoUiSelectComponent,
	type SelectOption,
} from '@framework/ui-lib/form-controls/jepto-ui-select/jepto-ui-select.component';

function dateRangeExpressionsEqual(
	left: DateRangeExpression | null | undefined,
	right: DateRangeExpression | null | undefined
): boolean {
	if (left == null && right == null) {
		return true;
	}
	if (left == null || right == null) {
		return false;
	}
	if (left.length !== right.length) {
		return false;
	}
	return left.every((token, index) => token === right[index]);
}

/** Default Jepto theme colors - light mode */
const JEPTO_COLORS_LIGHT = {
	backgroundColor: '#FFFFFF',
	borderColor: '#7D818A',
	borderSecondaryColor: '#E2E3E5',
	accentColor: '#651FFF',
};

/** Default Jepto theme colors - dark mode */
const JEPTO_COLORS_DARK = {
	backgroundColor: '#1A2036',
	borderColor: '#646A7F',
	borderSecondaryColor: '#363B4E',
	accentColor: '#AB7AFF',
};

interface UnitOption {
	value: DateRangeUnit;
	label: string;
	pluralLabel: string;
}

interface ISelectDateOption {
	id: string;
	label: string;
	isSelected?: boolean;
	isVisible?: boolean;
	startDate: Date;
	endDate: Date;
}

@Component({
	selector: 'jepto-date-range-picker',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		FormsModule,
		OverlayModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatButtonModule,
		MatListModule,
		MatSelectModule,
		MatRadioModule,
		MatDatepickerModule,
		JeptoUiSelectComponent,
		JeptoDateRangePickerPanelDirective,
	],
	templateUrl: './jepto-date-range-picker.component.html',
	styleUrl: './jepto-date-range-picker.component.scss',
	host: {
		'[style.--filter-background-color]': 'resolvedBackgroundColor()',
		'[style.--filter-border-color]': 'resolvedBorderColor()',
		'[style.--filter-border-secondary-color]':
			'resolvedBorderSecondaryColor()',
		'[style.--filter-accent-color]': 'resolvedAccentColor()',
		'[style.--filter-input-text-color]': 'resolvedInputTextColor()',
		'[style.--filter-label-text-color]': 'resolvedLabelTextColor()',
		'[style.--filter-font-family]': 'resolvedFontFamily()',
		/** Closed-trigger underline — theme border.secondary. */
		'[style.--picker-underline-color]': 'underlineBorderColor()',
		'[style.--Jepto---Light-border-primary]': 'jeptoLightBorderPrimary()',
		'[style.--Jepto---Dark-border-primary]': 'jeptoDarkBorderPrimary()',
		'[style.--Jepto---Light-text-primary]': 'jeptoLightTextPrimary()',
		'[style.--Jepto---Dark-text-primary]': 'jeptoDarkTextPrimary()',
		'[style.--Jepto---Light-text-link]': 'jeptoLightTextLink()',
		'[style.--Jepto---Dark-text-link]': 'jeptoDarkTextLink()',
	},
})
export class JeptoDateRangePickerComponent implements OnDestroy {
	/** Matches {@link _chart-editor-tokens.scss} `--chart-editor-font-family`. */
	private static readonly DEFAULT_FONT_FAMILY =
		"'Proxima Nova', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

	/** Preferred overlay positions: below then above, so the panel stays in viewport. */
	readonly overlayPositions: ConnectedPosition[] = [
		{
			originX: 'start',
			originY: 'bottom',
			overlayX: 'start',
			overlayY: 'top',
		},
		{
			originX: 'start',
			originY: 'top',
			overlayX: 'start',
			overlayY: 'bottom',
		},
	];

	private filterService = inject(FilterService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly themeService = inject(ThemeService);
	private readonly locale = inject(LOCALE_ID);
	private readonly injector = inject(Injector);
	private readonly panelHost =
		viewChild<JeptoDateRangePickerPanelDirective>('pickerPanel');
	private styleElement: HTMLStyleElement | null = null;
	private readonly styleId = `jepto-date-picker-${Math.random().toString(36).substring(2, 11)}`;
	/** Binds this picker's injected overlay CSS to its own panel (several pickers can coexist). */
	protected readonly panelScopeId = this.styleId;
	private readonly isBrowser = isPlatformBrowser(this.platformId);

	// --- Picker / theme inputs ---
	readonly dateFormat = input<string>(DEFAULT_DATE_RANGE_DISPLAY_FORMAT);
	readonly selectedPresetId = input<string>('lastMonth');
	/** Base floating label text (default `"Date Range"`). */
	readonly label = input<string>('Date Range');
	/**
	 * When true, appends the selected preset or range name in parentheses, e.g.
	 * `"Date Range (Last 7 days)"`. When false, only {@link label} is shown.
	 * Resolved dates always appear in the input via {@link displayValue}; they are
	 * not part of the floating label.
	 */
	readonly displaySelectedLabel = input<boolean>(true);
	readonly minDate = input<Date | undefined>(undefined);
	readonly maxDate = input<Date | undefined>(undefined);
	readonly presetOptions = input<DateRangePresetOption[]>(DEFAULT_PRESETS);
	readonly themeMode = input<ThemeMode>('auto');
	readonly themeConfig = input<ThemeConfig | undefined>(undefined);
	/**
	 * App chrome (drawers, editors): ignore the applied report theme and render with
	 * Jepto tokens so report theme colours and fonts cannot leak into the overlay.
	 */
	readonly useAppTheme = input<boolean>(false);
	readonly backgroundColor = input<string | undefined>(undefined);
	readonly borderColor = input<string | undefined>(undefined);
	readonly accentColor = input<string | undefined>(undefined);
	readonly enableDynamicRanges = input<boolean>(true);
	/**
	 * When false, date changes do not update {@link FilterService} (use for embedded pickers
	 * that only persist via `dateRangeChange`, e.g. chart card preview).
	 */
	readonly syncFilterService = input<boolean>(true);
	/**
	 * When set (e.g. parent supplies `cdkOverlayOrigin` on a visible trigger), the dropdown
	 * anchors to this element instead of the internal Material field — fixes placement in
	 * dialogs / transformed containers where the hidden trigger’s box is wrong.
	 */
	readonly connectedOverlayOrigin = input<CdkOverlayOrigin | null>(null);
	/** Root test id for Cypress/Chromatic; suffixes like -trigger, -panel, -preset-* are applied internally. */
	readonly testId = input<string>('jepto-date-range-picker');
	/**
	 * First day of the week for calendar columns and week-based presets (`thisWeek`, `lastWeek`).
	 * Accepts `'sunday' | 'monday'` or account strings `'Sunday to Saturday' | 'Monday to Sunday'`.
	 */
	readonly weekStart = input<WeekStartSetting>(DEFAULT_WEEK_START);
	/**
	 * When true, shows the "Start day of the Week" select in the panel footer.
	 * Set false when the parent owns week start (e.g. account setting) and should not expose it.
	 */
	readonly showWeekStartSelector = input<boolean>(true);
	/** Initial or restored {@link DateRangeExpression} (`["last30days"]` or `["2weeksAgo", "today"]`, etc.). */
	readonly dateRange = input<DateRangeExpression | undefined>(undefined);
	/** When true, the trigger is non-interactive (no panel open). */
	readonly disabled = input<boolean>(false);
	/** When true, render the trigger only (no panel); used on report edit grid. */
	readonly displayOnly = input<boolean>(false);
	/**
	 * When true, opening the panel keeps the closed value and label (e.g. "Last 30 days"
	 * or "Jan 1 2025 - Dec 31 2025") until the user picks a different range.
	 */
	readonly keepValueWhileOpen = input<boolean>(false);
	/**
	 * When true, the input always shows the date range and the matching preset name
	 * (including fixed dates that equal a preset, e.g. last year) moves to the label.
	 */
	readonly presetInLabel = input<boolean>(false);
	readonly dateRangeChange = output<DateRangeExpression>();
	/** Emits when the user applies a new week start from the panel footer. */
	readonly weekStartChange = output<WeekStart>();

	// --- Panel internal state ---
	readonly isOpen = signal(false);
	readonly activeView = signal<'presets' | 'calendar' | 'dynamic'>('presets');
	readonly activePresetId = signal<string | null>(null);
	readonly tempDateRange = signal<DateRange<Date> | null>(null);
	readonly tempSelectedOption = signal<ISelectDateOption | null>(null);
	readonly tempExpression = signal<DateRangeExpression | null>(null);
	/** Last expression applied via Done (uncontrolled mode when `dateRange` input is unset). */
	private readonly committedExpression = signal<DateRangeExpression | null>(
		null
	);
	/** Done apply waiting for controlled `[dateRange]` parent to catch up. */
	private pendingCommitExpression: DateRangeExpression | null = null;
	/**
	 * True once `[dateRange]` is bound to a valid expression. While controlled, clearing the
	 * input resets selection instead of falling back to {@link committedExpression}.
	 */
	private readonly isDateRangeControlled = signal(false);

	readonly leftMonth = signal(new Date());
	readonly rightMonth = signal(
		new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
	);

	readonly unitOptions: UnitOption[] = [
		{ value: 'day', label: 'Day', pluralLabel: 'Days' },
		{ value: 'week', label: 'Week', pluralLabel: 'Weeks' },
		{ value: 'month', label: 'Month', pluralLabel: 'Months' },
		{ value: 'quarter', label: 'Quarter', pluralLabel: 'Quarters' },
		{ value: 'year', label: 'Year', pluralLabel: 'Years' },
	];
	startType = signal<'relative' | 'fixed'>('relative');
	startOffset = signal<number>(30);
	startUnit = signal<DateRangeUnit>('day');
	fixedStartDate = signal<Date | null>(null);
	endOffset = signal<number>(0);
	endUnit = signal<DateRangeUnit>('day');
	endIsToday = signal<boolean>(true);

	/** Committed week start (panel footer + resolution when closed). */
	private readonly appliedWeekStart = signal<WeekStart>(
		coerceWeekStart(this.weekStart())
	);
	/** Week start while the panel is open; reverted on Cancel. */
	readonly tempWeekStart = signal<WeekStart>(
		coerceWeekStart(this.weekStart())
	);

	readonly panelWeekStartSelectOptions: SelectOption[] = [
		{ value: 'sunday', label: 'Sunday' },
		{ value: 'monday', label: 'Monday' },
	];

	readonly resolveOptions = computed(() => ({
		weekStart: this.isOpen()
			? this.tempWeekStart()
			: this.appliedWeekStart(),
	}));

	/**
	 * Week start only affects week-bound presets/expressions (Last week, N weeks ago, etc.).
	 * Disabled for month/day presets, fixed dates, and custom calendar ranges.
	 */
	readonly isWeekStartSelectorEnabled = computed(() => {
		if (this.activeView() === 'dynamic') {
			return expressionDependsOnWeekStart(
				this.dynamicPreviewExpression()
			);
		}
		if (this.activeView() === 'calendar') {
			return false;
		}
		const expr = this.tempExpression();
		if (expr) {
			return expressionDependsOnWeekStart(expr);
		}
		const presetId = this.activePresetId();
		return presetId === 'thisWeek' || presetId === 'lastWeek';
	});

	/** Sidebar highlight for saved/applied dynamic relative configs while on preset calendar view. */
	readonly isDynamicRangeSelectionActive = computed(() => {
		const expr = this.tempExpression();
		if (expr && isDynamicRangeExpression(expr)) {
			return true;
		}
		return false;
	});

	/** Recreate mat-calendar when week start, preset, or selection changes (Material does not refresh headers in place). */
	private readonly calendarRevision = signal(0);

	readonly activeCalendarWeekStart = computed(() =>
		this.isOpen() ? this.tempWeekStart() : this.appliedWeekStart()
	);

	readonly calendarFirstDayIndex = computed((): 0 | 1 =>
		getFirstDayOfWeekIndex(this.activeCalendarWeekStart())
	);

	/** Day-normalized range for mat-calendar highlighting and month sync. */
	readonly calendarDisplayRange = computed(() => {
		const range = this.tempDateRange();
		if (!range?.start) {
			return null;
		}
		return new DateRange(
			this.startOfDayLocal(range.start),
			range.end ? this.startOfDayLocal(range.end) : null
		);
	});

	private readonly calendarSelectionKey = computed(() => {
		const range = this.calendarDisplayRange();
		if (!range?.start) {
			return 'empty';
		}
		if (!range.end) {
			return `${range.start.getTime()}-partial`;
		}
		return `${range.start.getTime()}-${range.end.getTime()}`;
	});

	readonly leftCalendarTrack = computed(
		() =>
			`l-${this.leftMonth().getTime()}-${this.activeCalendarWeekStart()}-${this.calendarSelectionKey()}-${this.activePresetId() ?? ''}-${this.calendarRevision()}`
	);
	readonly rightCalendarTrack = computed(
		() =>
			`r-${this.rightMonth().getTime()}-${this.activeCalendarWeekStart()}-${this.calendarSelectionKey()}-${this.activePresetId() ?? ''}-${this.calendarRevision()}`
	);

	/**
	 * Slot wrappers so `@for track` identity changes when the visible month/selection
	 * changes. MatCalendar only applies `[startAt]` in `ngAfterContentInit`, so the
	 * view must be recreated (or `activeDate` set) when presets jump far (e.g. Last Year).
	 */
	readonly leftCalendarSlots = computed(() => [
		{ startAt: this.leftMonth(), trackId: this.leftCalendarTrack() },
	]);
	readonly rightCalendarSlots = computed(() => [
		{ startAt: this.rightMonth(), trackId: this.rightCalendarTrack() },
	]);

	/** Open overlay calendars — used to force month navigation after preset changes. */
	private readonly matCalendars = viewChildren(MatCalendar);

	selectedDateRange: DateRange<Date> | undefined;

	private readonly isDark = computed(() => {
		const mode = this.themeMode();
		if (mode === 'dark') return true;
		if (mode === 'light') return false;
		return this.themeService.getActiveThemeModeSignal()() === 'dark';
	});

	/**
	 * Prefer ThemeService’s applied report theme (same source as `:root`
	 * `--text-primary-color` / `--border-secondary-color` used by chart/table).
	 * Input alone can be a truthy DEFAULT_THEME and hide custom colors.
	 */
	private readonly effectiveThemeConfig = computed(() => {
		if (this.useAppTheme()) {
			return undefined;
		}
		const applied = this.themeService.getAppliedThemeConfig();
		if (applied) {
			return applied;
		}
		return this.themeConfig() ?? this.themeService.getThemeConfig();
	});

	protected readonly resolvedBackgroundColor = computed(() => {
		const theme = this.effectiveThemeConfig();
		if (theme) {
			const mode = this.isDark() ? theme.colors.dark : theme.colors.light;
			return (
				mode.card?.background ??
				mode.background ??
				(this.isDark()
					? JEPTO_COLORS_DARK.backgroundColor
					: JEPTO_COLORS_LIGHT.backgroundColor)
			);
		}
		const custom = this.backgroundColor();
		if (custom) return custom;
		return this.isDark()
			? JEPTO_COLORS_DARK.backgroundColor
			: JEPTO_COLORS_LIGHT.backgroundColor;
	});

	protected readonly resolvedBorderColor = computed(() => {
		const theme = this.effectiveThemeConfig();
		if (theme) {
			const mode = this.isDark() ? theme.colors.dark : theme.colors.light;
			return (
				mode.border?.primary ??
				mode.border?.secondary ??
				(this.isDark()
					? JEPTO_COLORS_DARK.borderColor
					: JEPTO_COLORS_LIGHT.borderColor)
			);
		}
		const custom = this.borderColor();
		if (custom) return custom;
		return this.isDark()
			? JEPTO_COLORS_DARK.borderColor
			: JEPTO_COLORS_LIGHT.borderColor;
	});

	/** Theme `border.secondary` — closed-trigger underline + panel dividers. */
	protected readonly resolvedBorderSecondaryColor = computed(() => {
		const theme = this.effectiveThemeConfig();
		if (theme) {
			const mode = this.isDark() ? theme.colors.dark : theme.colors.light;
			return (
				mode.border?.secondary ??
				mode.border?.primary ??
				(this.isDark()
					? JEPTO_COLORS_DARK.borderSecondaryColor
					: JEPTO_COLORS_LIGHT.borderSecondaryColor)
			);
		}
		const custom = this.borderColor();
		if (custom) return custom;
		return this.isDark()
			? JEPTO_COLORS_DARK.borderSecondaryColor
			: JEPTO_COLORS_LIGHT.borderSecondaryColor;
	});

	protected readonly resolvedAccentColor = computed(() => {
		const theme = this.effectiveThemeConfig();
		if (theme) {
			const mode = this.isDark() ? theme.colors.dark : theme.colors.light;
			return (
				mode.text?.link ??
				(this.isDark()
					? JEPTO_COLORS_DARK.accentColor
					: JEPTO_COLORS_LIGHT.accentColor)
			);
		}
		const custom = this.accentColor();
		if (custom) return custom;
		return this.isDark()
			? JEPTO_COLORS_DARK.accentColor
			: JEPTO_COLORS_LIGHT.accentColor;
	});

	protected readonly resolvedInputTextColor = computed(() =>
		this.resolvedTextPrimaryColor()
	);

	protected readonly resolvedLabelTextColor = computed(() =>
		this.resolvedTextPrimaryColor()
	);

	/** Theme `text.primary` — closed trigger value, label, and calendar icon. */
	protected readonly resolvedTextPrimaryColor = computed(() => {
		const theme = this.effectiveThemeConfig();
		if (theme) {
			const mode = this.isDark() ? theme.colors.dark : theme.colors.light;
			const fromTheme = mode.text?.primary?.trim();
			if (fromTheme) {
				return fromTheme;
			}
		}
		return this.isDark()
			? 'rgba(255, 255, 255, 0.87)'
			: 'rgba(15, 21, 38, 0.82)';
	});

	/** Closed-trigger underline — theme `border.secondary`. */
	protected readonly underlineBorderColor = computed(() =>
		this.resolvedBorderSecondaryColor()
	);

	/** Figma token — set on host so other strokes can read Jepto border primary. */
	protected readonly jeptoLightBorderPrimary = computed(() =>
		this.isDark() ? null : this.resolvedBorderColor()
	);

	protected readonly jeptoDarkBorderPrimary = computed(() =>
		this.isDark() ? this.resolvedBorderColor() : null
	);

	protected readonly jeptoLightTextPrimary = computed(() =>
		this.isDark() ? null : this.resolvedTextPrimaryColor()
	);

	protected readonly jeptoDarkTextPrimary = computed(() =>
		this.isDark() ? this.resolvedTextPrimaryColor() : null
	);

	protected readonly jeptoLightTextLink = computed(() =>
		this.isDark() ? null : this.resolvedAccentColor()
	);

	protected readonly jeptoDarkTextLink = computed(() =>
		this.isDark() ? this.resolvedAccentColor() : null
	);

	/** Proxima stack via `--filter-font-family` (theme typography or chart-editor token). */
	protected readonly resolvedFontFamily = computed(() => {
		const theme = this.effectiveThemeConfig();
		const family = theme?.typography?.fontFamily?.trim();
		if (family) {
			return family.includes(',')
				? family
				: `'${family}', Helvetica, Arial, sans-serif`;
		}
		if (this.isBrowser && typeof document !== 'undefined') {
			const fromChartEditor = getComputedStyle(document.documentElement)
				.getPropertyValue('--chart-editor-font-family')
				.trim();
			if (fromChartEditor) {
				return fromChartEditor;
			}
			const fromRoot = getComputedStyle(document.documentElement)
				.getPropertyValue('--font-family')
				.trim();
			if (fromRoot) {
				return fromRoot;
			}
		}
		return JeptoDateRangePickerComponent.DEFAULT_FONT_FAMILY;
	});

	readonly fixedStartDateForPicker = computed(() => {
		const d = this.fixedStartDate();
		if (d) return d;
		const def = new Date();
		def.setDate(def.getDate() - 30);
		def.setHours(0, 0, 0, 0);
		return def;
	});

	readonly dynamicPreviewExpression = computed<DateRangeExpression>(() => {
		if (this.startType() === 'relative') {
			return expressionFromDynamicRelativeForm(
				this.startOffset(),
				this.startUnit(),
				this.endIsToday(),
				this.endOffset(),
				this.endUnit()
			);
		}
		return expressionFromFixedAndEnd(
			this.getFixedStartDate(),
			this.getRelativeEndDate()
		);
	});

	readonly dynamicPreviewDates = computed(() => {
		const expression = this.dynamicPreviewExpression();
		const resolved = tryResolveDateRangeExpression(
			expression,
			this.resolveOptions()
		);
		if (!resolved) {
			return { start: '', end: '' };
		}
		return {
			start: formatDateToISO(resolved.startDate),
			end: formatDateToISO(resolved.endDate),
		};
	});

	readonly dynamicPreviewLabel = computed(() => this.buildDynamicLabel());

	readonly dynamicRangeValidationError = computed(() => {
		const expression = this.dynamicPreviewExpression();
		if (tryResolveDateRangeExpression(expression, this.resolveOptions())) {
			return null;
		}
		if (!isValidDateRangeExpression(expression)) {
			return null;
		}
		return 'End date must be on or after start date';
	});

	/** Input always shows the formatted date range, except when a preset label applies (e.g. All time). */
	readonly displayValue = computed(() => {
		if (!this.isOpen() || this.#keepClosedPresentation()) {
			return this.#closedDisplayValue();
		}
		const range = this.tempDateRange() ?? this.selectedDateRange;
		if (!range?.start || !range?.end) return '';
		return `${this.formatDate(range.start)} - ${this.formatDate(range.end)}`;
	});

	#closedDisplayValue(): string {
		const matchedLabel = this.presetInLabel()
			? null
			: this.#matchingPresetLabel(this.#closedExpression());
		if (matchedLabel) {
			return matchedLabel;
		}
		const range =
			this.#resolveClosedDisplayDateRange() ?? this.selectedDateRange;
		if (!range?.start || !range?.end) return '';
		return `${this.formatDate(range.start)} - ${this.formatDate(range.end)}`;
	}

	#closedExpression(): DateRangeExpression | undefined {
		return (
			parseDateRangeExpressionInput(this.dateRange()) ??
			this.committedExpression()
		);
	}

	/** {@link keepValueWhileOpen}: open panel whose pending range still equals the closed range. */
	#keepClosedPresentation(): boolean {
		if (!this.isOpen() || !this.keepValueWhileOpen()) {
			return false;
		}
		const pending = this.tempDateRange() ?? this.selectedDateRange;
		const closed =
			this.#resolveClosedDisplayDateRange() ?? this.selectedDateRange;
		if (!pending?.start || !pending?.end || !closed?.start || !closed?.end) {
			return true;
		}
		return (
			this.formatDate(pending.start) === this.formatDate(closed.start) &&
			this.formatDate(pending.end) === this.formatDate(closed.end)
		);
	}

	/** {@link presetInLabel}: preset whose dates equal the closed range (fixed ISO ranges). */
	#closedRangePresetLabel(): string | null {
		if (!this.presetInLabel()) {
			return null;
		}
		const range =
			this.#resolveClosedDisplayDateRange() ?? this.selectedDateRange;
		if (!range?.start || !range?.end) {
			return null;
		}
		return (
			this.findPresetMatchingRange(this.presetOptions(), range)?.label ??
			null
		);
	}

	#matchingPresetLabel(expression: DateRangeExpression | undefined): string | null {
		if (!expression) {
			return null;
		}
		const match = this.presetOptions().find(
			preset =>
				preset.id !== 'custom' &&
				dateRangeExpressionsEqual(preset.expression, expression)
		);
		return match?.label ?? null;
	}

	/** Floating mat-label: base {@link label} with optional preset/range suffix. Never show the suffix for fixed ISO start expressions unless a provided preset matches (e.g. All time). */
	readonly fieldLabel = computed(() => {
		const base = this.label();
		if (!this.displaySelectedLabel()) return base;

		if (!this.isOpen() || this.#keepClosedPresentation()) {
			const expression = this.#closedExpression();
			const matched =
				this.#matchingPresetLabel(expression) ??
				this.#closedRangePresetLabel();
			if (matched) {
				return `${base} (${matched})`;
			}
			if (
				expression?.length === 2 &&
				isIsoDateToken(expression[0])
			) {
				return base;
			}
			if (expression) {
				return `${base} (${labelForDateRangeExpression(expression, this.resolveOptions())})`;
			}
			return base;
		}

		const expr = this.tempExpression();
		const openMatched = this.#matchingPresetLabel(expr);
		if (openMatched) {
			return `${base} (${openMatched})`;
		}
		if (expr?.length === 2 && isIsoDateToken(expr[0])) return base;
		const opt = this.tempSelectedOption();
		if (opt?.label) return `${base} (${opt.label})`;
		if (expr)
			return `${base} (${labelForDateRangeExpression(expr, this.resolveOptions())})`;
		const id = this.activePresetId();
		const preset = id ? this.presetOptions().find(p => p.id === id) : null;
		if (preset?.label) return `${base} (${preset.label})`;
		return base;
	});

	private formatDate(date: Date): string {
		return formatDateRangeDisplayDate(date, this.locale, this.dateFormat());
	}

	constructor() {
		effect(() => {
			this.appliedWeekStart.set(coerceWeekStart(this.weekStart()));
		});

		effect(() => {
			if (!this.isOpen()) {
				return;
			}
			if (!this.panelHost()) {
				return;
			}
			this.applyWeekStartToCalendarAdapter(
				this.activeCalendarWeekStart()
			);
		});

		effect(() => {
			const inputExpression = parseDateRangeExpressionInput(
				this.dateRange()
			);
			if (
				this.pendingCommitExpression &&
				inputExpression &&
				dateRangeExpressionsEqual(
					inputExpression,
					this.pendingCommitExpression
				)
			) {
				this.pendingCommitExpression = null;
			}
			if (
				inputExpression &&
				!this.pendingCommitExpression &&
				this.committedExpression()
			) {
				const committed = this.committedExpression();
				if (
					committed &&
					!dateRangeExpressionsEqual(committed, inputExpression)
				) {
					this.committedExpression.set(null);
				}
			}
			this.committedExpression();
			this.appliedWeekStart();
			if (this.isOpen()) {
				return;
			}
			this.syncSelectionFromSources();
		});

		effect(() => {
			void this.isDark();
			void this.resolvedFontFamily();
			this.updateOverlayStyles();
		});

		effect(() => {
			if (!this.isOpen()) return;
			const presets = this.presetOptions();
			const id = this.activePresetId();
			const preset = id ? presets.find(p => p.id === id) : null;
			if (preset && preset.id !== 'custom') {
				this.tempDateRange.set(this.resolvePresetRange(preset));
				this.tempSelectedOption.set(this.optionFromPreset(preset));
				this.tempExpression.set(preset.expression);
			} else if (preset?.id === 'custom') {
				this.tempSelectedOption.set(null);
				const range = this.tempDateRange();
				if (range?.start && !range?.end) {
					this.tempExpression.set(null);
					return;
				}
				if (!range?.start) {
					this.tempDateRange.set(this.resolvePresetRange(preset));
					this.tempExpression.set(preset.expression);
				}
			} else if (id) {
				const fallback =
					presets.find(p => p.id === id) ??
					ALL_PRESETS.find(p => p.id === id);
				if (fallback && fallback.id !== 'custom') {
					this.tempDateRange.set(this.resolvePresetRange(fallback));
					this.tempSelectedOption.set(
						this.optionFromPreset(fallback)
					);
					this.tempExpression.set(fallback.expression);
					return;
				}
				// Preset rail shows a selection but options list has no matching id — do not
				// restore `selectedDateRange` (e.g. dynamic 10MonthsAgo/today) over selectPreset().
				return;
			} else {
				const dates = this.selectedDateRange;
				const range =
					dates?.start && dates?.end
						? new DateRange(dates.start, dates.end)
						: null;
				this.tempDateRange.set(range);
				const expr = this.tempExpression();
				if (expr && isDynamicRangeExpression(expr)) {
					return;
				}
				this.tempExpression.set(null);
				this.tempSelectedOption.set(null);
			}
		});

		effect(() => {
			this.syncCalendarMonthsFromRange(this.calendarDisplayRange());
		});

		effect(() => {
			const expr = this.tempExpression();
			if (!expr || expr.length !== 2) {
				return;
			}
			const [startToken, endToken] = expr;
			if (isIsoDateToken(startToken)) {
				this.startType.set('fixed');
				this.fixedStartDate.set(parseISODate(startToken));
				this.endIsToday.set(endToken === 'today');
				if (endToken !== 'today') {
					const endRel = parseRelativeAgoToken(endToken);
					if (endRel) {
						this.endOffset.set(endRel.count);
						this.endUnit.set(relativeUnitToFormUnit(endRel.unit));
					} else if (isIsoDateToken(endToken)) {
						const endDate = parseISODate(endToken);
						const today = new Date();
						today.setHours(0, 0, 0, 0);
						endDate.setHours(0, 0, 0, 0);
						const days = Math.round(
							(today.getTime() - endDate.getTime()) /
								(24 * 60 * 60 * 1000)
						);
						this.endOffset.set(Math.max(0, days));
						this.endUnit.set('day');
					}
				}
				return;
			}
			const startRel = parseRelativeAgoToken(startToken);
			if (startRel) {
				this.startType.set('relative');
				this.startOffset.set(startRel.count);
				this.startUnit.set(relativeUnitToFormUnit(startRel.unit));
				this.endIsToday.set(endToken === 'today');
				if (endToken !== 'today') {
					const endRel = parseRelativeAgoToken(endToken);
					if (endRel) {
						this.endOffset.set(endRel.count);
						this.endUnit.set(relativeUnitToFormUnit(endRel.unit));
					}
				}
			}
		});

		effect(() => {
			if (!this.isOpen()) {
				return;
			}
			// Track reactive deps here (reactive context); defer DOM writes to
			// afterNextRender to avoid calling effect() or inject() in a phase
			// that has no injection context (afterRenderEffect is experimental
			// and its callback lacks an injection context, which causes NG0203
			// when any downstream code — including Material internals — calls
			// effect() or inject()).
			void this.leftMonth();
			void this.rightMonth();
			void this.calendarRevision();
			void this.matCalendars();
			afterNextRender(
				() => {
					this.applyCalendarActiveDates();
					this.applyDualPanelCalendarA11y();
				},
				{ injector: this.injector }
			);
		});
	}

	ngOnDestroy(): void {
		this.removeOverlayStyles();
	}

	private getThemeFontFamily(): string {
		return this.resolvedFontFamily();
	}

	private updateOverlayStyles(): void {
		if (!this.isBrowser) return;

		const bgColor = this.resolvedBackgroundColor();
		const borderColor = this.resolvedBorderColor();
		const borderSecondaryColor = this.resolvedBorderSecondaryColor();
		const accentColor = this.resolvedAccentColor();
		const dark = this.isDark();
		const fontFamily = this.getThemeFontFamily();

		this.removeOverlayStyles();

		const accentColorLight = this.hexToRgba(accentColor, 0.2);
		/** Active preset background: theme accent at low opacity (works for any custom theme) */
		const accentBgLight = this.hexToRgba(accentColor, 0.12);
		const accentBgDark = this.hexToRgba(accentColor, 0.18);

		const textColor = dark ? 'rgba(255, 255, 255, 0.87)' : '';
		const secondaryText = dark ? 'rgba(255, 255, 255, 0.6)' : '';
		const ink54 = dark
			? 'rgba(255, 255, 255, 0.54)'
			: 'rgba(15, 21, 38, 0.54)';
		const ink82 = dark
			? 'rgba(255, 255, 255, 0.87)'
			: 'rgba(15, 21, 38, 0.82)';
		const ink12 = dark
			? 'rgba(255, 255, 255, 0.12)'
			: 'rgba(15, 21, 38, 0.12)';
		const fieldBorderColor = dark
			? 'rgba(255, 255, 255, 0.38)'
			: 'rgba(15, 21, 38, 0.12)';
		const calendarText = dark
			? `
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar {
        background: ${bgColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-period-button,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body th,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body thead .mat-calendar-body-cell-content,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar button,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar .mat-calendar-arrow {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-table-header th,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-table-header .mat-calendar-body-cell-content {
        color: ${ink54} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-cell:not(.mat-calendar-body-disabled) .mat-calendar-body-cell-content {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-cell-content.mat-calendar-body-selected {
        color: #fff !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-cell:not(.mat-calendar-body-selected):not(.mat-calendar-body-in-range):not(.mat-calendar-body-disabled) .mat-calendar-body-cell-content {
        color: ${secondaryText} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-disabled .mat-calendar-body-cell-content {
        color: ${ink54} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-mdc-list-item .mdc-list-item__primary-text {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) button.mat-mdc-button:not(.picker-panel__cancel) {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-cell:not(.mat-calendar-body-disabled):hover .mat-calendar-body-cell-content {
        background: rgba(255, 255, 255, 0.08) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar .mat-icon {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__content,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form {
        color: ${textColor};
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mat-mdc-input-element,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mdc-floating-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mat-mdc-select-value,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-label {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__form-title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__section-label:not(.picker-panel__section-label--emphasis),
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section__title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section__body {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__section-label--emphasis {
        font-size: 0.8rem !important;
        font-weight: 600 !important;
        text-transform: uppercase !important;
        color: ${secondaryText || 'rgba(15, 21, 38, 0.54)'} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-radio__native-control:not(:checked) + .mdc-radio__background .mdc-radio__outer-circle {
        border-color: rgba(255, 255, 255, 0.5) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mdc-notched-outline .mdc-notched-outline__leading,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mdc-notched-outline .mdc-notched-outline__notch,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mdc-notched-outline .mdc-notched-outline__trailing {
        border-color: rgba(255, 255, 255, 0.38) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-form-field .mdc-notched-outline .mdc-floating-label {
        color: ${secondaryText} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section {
        border-color: #42a5f5 !important;
        background: rgba(66, 165, 245, 0.12) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section--error {
        border-color: #f3403d !important;
        background: rgba(243, 64, 61, 0.12) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section--error .picker-panel__preview-section__title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__preview-section--error .picker-panel__preview-section__body {
        color: rgba(100, 31, 29, 0.92) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mat-mdc-floating-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mdc-floating-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mat-mdc-form-field-label {
        color: ${ink54} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mat-mdc-select-value-text,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mat-mdc-select-min-line,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .jepto-ui-select__trigger-label {
        color: ${textColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mat-mdc-select-arrow-wrapper::after {
        color: ${ink54} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mdc-notched-outline__leading,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mdc-notched-outline__notch,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field .mdc-notched-outline__trailing {
        border-color: ${fieldBorderColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field.mat-mdc-form-field-disabled .mat-mdc-select-value-text,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field.mat-form-field-disabled .mat-mdc-select-value-text {
        color: ${ink54} !important;
      }
    `
			: '';

		this.styleElement = document.createElement('style');
		this.styleElement.id = this.styleId;
		const darkVars = dark
			? `
        --background-secondary-color: rgba(255, 255, 255, 0.08);
        --text-primary-color: rgba(255, 255, 255, 0.87);
        --text-secondary-color: rgba(255, 255, 255, 0.6);
      `
			: '';

		const panelLayout = `
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel {
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        background: var(--filter-background-color, #fff);
        border: 1px solid var(--picker-divider-color, var(--filter-border-secondary-color, var(--border-secondary-color, #e2e3e5)));
        border-radius: 8px;
        box-shadow: 0 4px 12px var(--filter-shadow-color, rgba(101, 31, 255, 0.12));
        min-width: 800px;
        max-height: min(85vh, 720px);
        overflow-x: hidden;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        --picker-panel-body-height: 386px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__body {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        flex-shrink: 0;
        min-height: var(--picker-panel-body-height, 386px);
        overflow: visible;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .mdc-list-item__primary-text,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .mat-calendar-period-button,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .mat-calendar-period-button .mdc-button__label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__form-title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__section-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__preview-section__title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__preview-section__body,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__validation-error,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .mat-mdc-radio-button .mdc-label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__dynamic-field .mat-mdc-input-element,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__dynamic-field .mat-mdc-select-value,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel .picker-panel__dynamic-field .mdc-floating-label {
        line-height: var(--picker-text-line-height, 1) !important;
        letter-spacing: var(--picker-text-letter-spacing, 0) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__presets {
        width: 180px;
        min-width: 180px;
        flex-shrink: 0;
        border-right: 1px solid var(--filter-border-secondary-color, var(--border-secondary-color, #e2e3e5));
        padding: 16px 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__presets .mat-mdc-list-item {
        min-height: 32px;
        height: 32px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__presets .mdc-list-item__primary-text {
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: 14px;
        color: var(--picker-text-primary-color, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82))) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__content {
        flex: 1 1 auto;
        min-width: 0;
        padding: 16px;
        overflow: visible;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dual-calendar {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__calendar-month {
        flex: 1;
        min-width: 280px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-calendar-controls {
        gap: 10px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-calendar-period-button {
        margin-left: 0;
        margin-right: 0;
        --mat-datepicker-calendar-period-button-text-weight: 400;
        --mat-datepicker-calendar-period-button-text-size: 14px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-calendar-period-button,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-calendar-period-button .mdc-button__label {
        color: var(--picker-text-primary-color, var(--picker-calendar-period-color, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82)))) !important;
        font-family: var(--filter-font-family, var(--chart-editor-font-family)) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        text-transform: capitalize !important;
      }
      /* Calendar header: prev/next are icon buttons — global theme often inflates touch target to ~56px */
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button {
        --mat-icon-button-state-layer-size: 0px;
        --mat-icon-button-touch-target-size: 0px;
        --mat-icon-button-icon-size: 20px;
        width: auto !important;
        height: auto !important;
        min-width: 0 !important;
        padding: 0 !important;
        border-radius: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        flex-shrink: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button .mat-mdc-button-touch-target,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button .mat-mdc-button-touch-target,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button .mat-mdc-button-ripple,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button .mat-mdc-button-ripple,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button .mdc-icon-button__ripple,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button .mdc-icon-button__ripple {
        display: none !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button .mat-mdc-button-persistent-ripple::before,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button .mat-mdc-button-persistent-ripple::before,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-previous-button .mat-mdc-focus-indicator::before,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-header .mat-mdc-icon-button.mat-calendar-next-button .mat-mdc-focus-indicator::before {
        opacity: 0 !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form {
        padding: 0;
        min-width: 280px;
        max-width: 560px;
        min-height: 100%;
        box-sizing: border-box;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__form-title {
        margin: 0 0 16px 0;
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: var(--chart-editor-font-size-label, 13px);
        font-weight: 600;
        color: var(--text-primary-color, var(--picker-ink-82, rgba(15, 21, 38, 0.82)));
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__start-end-row {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 24px;
        align-items: flex-start;
        margin-bottom: 16px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__start-end-row > .picker-panel__form-section {
        flex: 1 1 0;
        min-width: 140px;
        margin-bottom: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__form-section {
        margin-bottom: 16px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__section-label {
        display: block;
        margin-bottom: 8px;
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: var(--chart-editor-font-size-tab, 12px);
        font-weight: 500;
        color: var(--text-secondary-color, var(--picker-ink-54, rgba(15, 21, 38, 0.54)));
        text-transform: uppercase;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__section-label--emphasis {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--text-secondary-color, var(--picker-ink-54, rgba(15, 21, 38, 0.54)));
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__start-toggle,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__end-toggle {
        margin-bottom: 12px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-label {
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: var(--chart-editor-font-size-label, 13px);
        font-weight: 400;
        color: var(--picker-ink-82, rgba(15, 21, 38, 0.82));
        padding-left: 0;
        padding-right: 20px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__input-row {
        display: flex;
        gap: 8px;
        align-items: flex-start;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__fixed-date-row {
        width: 100%;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-form-field-subscript-wrapper {
        display: none !important;
        height: 0 !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-text-field-wrapper {
        padding: 0;
        min-height: 40px;
        height: 40px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-form-field-flex {
        align-items: center;
        height: 100%;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-form-field-infix {
        display: flex;
        align-items: center;
        min-height: 40px !important;
        height: 40px;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        box-sizing: border-box;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-input-element {
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: var(--chart-editor-font-size-input, 14px);
        padding: 0;
        margin: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-select {
        width: 100%;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-select-trigger {
        display: inline-flex;
        align-items: center;
        height: 40px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-select-value {
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
        font-size: var(--chart-editor-font-size-input, 14px);
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mdc-notched-outline .mdc-notched-outline__leading,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mdc-notched-outline .mdc-notched-outline__notch,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mdc-notched-outline .mdc-notched-outline__trailing {
        border-color: var(--picker-field-border-color, rgba(15, 21, 38, 0.12)) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field.mat-mdc-form-field.mat-focused .mdc-notched-outline .mdc-notched-outline__leading,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field.mat-mdc-form-field.mat-focused .mdc-notched-outline .mdc-notched-outline__notch,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field.mat-mdc-form-field.mat-focused .mdc-notched-outline .mdc-notched-outline__trailing {
        border-color: var(--picker-accent-color, var(--filter-accent-color, var(--jep-color-primary, #651fff))) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__dynamic-field .mat-mdc-form-field-icon-suffix {
        align-self: center;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__offset-input {
        width: 80px;
        flex-shrink: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__offset-input .mat-mdc-form-field-infix {
        padding-left: 12px;
        padding-right: 12px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select {
        flex: 1;
        min-width: 120px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select .mat-mdc-form-field-infix {
        padding-left: 0;
        padding-right: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select .mat-mdc-select-trigger {
        padding-left: 12px;
        padding-right: 12px;
        box-sizing: border-box;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select .mat-mdc-select-arrow svg {
        display: none !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select .mat-mdc-select-arrow {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__unit-select .mat-mdc-select-arrow::after {
        content: 'expand_more';
        font-family: 'Material Symbols Outlined', sans-serif;
        font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        font-size: 20px;
        line-height: 1;
        color: var(--picker-ink-82, rgba(15, 21, 38, 0.82));
        font-style: normal;
        font-weight: normal;
        letter-spacing: normal;
        text-transform: none;
        white-space: nowrap;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input {
        flex: 1 1 auto;
        width: 100%;
        min-width: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-mdc-form-field-infix {
        padding-left: 12px;
        padding-right: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-mdc-form-field-icon-suffix {
        display: flex;
        align-items: center;
        align-self: center;
        height: 40px;
        padding: 0;
        margin: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        line-height: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle .mat-mdc-icon-button.mat-mdc-button-base {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        min-height: 40px !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
        background: transparent !important;
        border: none !important;
        border-radius: 0 !important;
        vertical-align: middle;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle .mat-mdc-button-touch-target,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle .mat-mdc-button-ripple,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle .mdc-icon-button__ripple {
        display: none !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .picker-panel__date-input .mat-datepicker-toggle .mat-icon {
        display: flex !important;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        font-size: 20px;
        line-height: 20px;
        margin: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section {
        box-sizing: border-box;
        width: 100%;
        padding: 8px 16px;
        margin-bottom: 0;
        border: 1px solid #42a5f5;
        border-radius: 2px;
        background-color: rgba(66, 165, 245, 0.12);
        font-family: var(--filter-font-family, var(--chart-editor-font-family));
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section__title {
        margin: 0;
        padding: 0;
        font-size: 14px;
        font-weight: 600;
        color: rgba(15, 21, 38, 0.82);
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section__body {
        margin: 0;
        padding: 0;
        font-size: 13px;
        font-weight: 400;
        color: rgba(15, 21, 38, 0.82);
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section--error {
        border-color: #f3403d;
        background-color: rgba(243, 64, 61, 0.12);
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section--error .picker-panel__preview-section__title,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__preview-section--error .picker-panel__preview-section__body {
        color: rgba(100, 31, 29, 0.92);
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__validation-error {
        margin: 8px 0 0 0;
        font-size: 12px;
        color: var(--status-danger-color, #b71c1c);
        font-weight: 500;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
        gap: 24px;
        padding: 12px 16px;
        border-top: 1px solid var(--filter-border-secondary-color, var(--border-secondary-color, #e2e3e5));
        box-sizing: border-box;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions-leading {
        display: flex;
        flex: 0 0 auto;
        justify-content: flex-start;
        align-items: flex-start;
        min-width: 0;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__week-start-field {
        display: block;
        flex: 0 0 280px;
        width: 280px;
        max-width: 280px;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__action-buttons {
        display: flex;
        flex: 0 0 auto;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 9px;
        margin-left: auto;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel.mat-mdc-button,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-unelevated-button {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 0 !important;
        height: auto !important;
        padding: 10px 24px !important;
        border-radius: 2px !important;
        text-align: center !important;
        font-family: var(--filter-font-family, 'Proxima Nova', sans-serif) !important;
        font-size: 12px !important;
        font-style: normal !important;
        font-weight: 600 !important;
        line-height: 16px !important;
        letter-spacing: 1px !important;
        text-transform: uppercase !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel.mat-mdc-button {
        border: none !important;
        background: transparent !important;
        box-shadow: none !important;
        color: var(--filter-accent-color, #651fff) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel.mat-mdc-button:hover,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel.mat-mdc-button:focus,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel.mat-mdc-button:active {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: var(--filter-accent-color, #651fff) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-unelevated-button {
        border: none !important;
        background: var(--filter-accent-color, #651fff) !important;
        box-shadow: 0 1px 2.5px rgba(15, 21, 38, 0.12), 0 2px 1px rgba(15, 21, 38, 0.14), 0 0 0.5px rgba(15, 21, 38, 0.2) !important;
        color: #fff !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-unelevated-button:not([disabled]):hover,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-unelevated-button:not([disabled]):focus,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-unelevated-button:not([disabled]):active {
        background: var(--filter-accent-color, #651fff) !important;
        box-shadow: 0 1px 2.5px rgba(15, 21, 38, 0.12), 0 2px 1px rgba(15, 21, 38, 0.14), 0 0 0.5px rgba(15, 21, 38, 0.2) !important;
        color: #fff !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done[disabled],
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done.mat-mdc-button-disabled {
        opacity: 0.48;
        box-shadow: none !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__cancel .mdc-button__label,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__actions .picker-panel__done .mdc-button__label {
        font-family: inherit !important;
        font-size: inherit !important;
        font-weight: inherit !important;
        line-height: inherit !important;
        letter-spacing: inherit !important;
        text-transform: inherit !important;
        color: inherit !important;
      }
    `;

		const overlayCss = `
      .cdk-overlay-pane:has(.custom-ckd-container) {
        --filter-background-color: ${bgColor};
        --filter-border-color: ${borderColor};
        --filter-border-secondary-color: ${borderSecondaryColor};
        --filter-accent-color: ${accentColor};
        --filter-font-family: ${fontFamily};
        --picker-calendar-period-color: ${dark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(15, 21, 38, 0.82)'};
        ${
			dark
				? `--Jepto---Dark-border-primary: ${borderColor};
        --Jepto---Dark-text-primary: rgba(255, 255, 255, 0.87);
        --Jepto---Dark-text-link: ${accentColor};`
				: `--Jepto---Light-border-primary: ${borderColor};
        --Jepto---Light-text-primary: rgba(15, 21, 38, 0.82);
        --Jepto---Light-text-link: ${accentColor};`
		}
        --picker-text-primary-color: var(--Jepto---Dark-text-primary, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82)));
        --picker-divider-color: var(--filter-border-secondary-color, var(--border-secondary-color, #e2e3e5));
        --picker-text-line-height: 1;
        --picker-text-letter-spacing: 0;
        --picker-accent-color: var(--filter-accent-color, var(--jep-color-primary, #651fff));
        --picker-field-border-color: ${fieldBorderColor};
        --picker-ink-82: ${ink82};
        --picker-ink-54: ${ink54};
        --chart-editor-ink-82: ${ink82};
        --chart-editor-ink-54: ${ink54};
        --chart-editor-ink-12: ${ink12};
        --filter-shadow-color: ${dark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(101, 31, 255, 0.12)'};
        --background-primary-color: ${bgColor};
        --card-background-color: ${bgColor};
        --card-border-color: ${borderColor};
        --border-primary-color: ${borderColor};
        --border-secondary-color: ${borderSecondaryColor};
        --text-link-color: ${accentColor};
        font-family: ${fontFamily};
        ${darkVars}
      }
      ${panelLayout}
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar {
        font-family: var(--filter-font-family) !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar {
        --mat-datepicker-calendar-period-button-text-weight: 400;
        --mat-datepicker-calendar-period-button-text-size: 14px;
        --mat-datepicker-calendar-period-button-text-color: var(--picker-text-primary-color, var(--picker-calendar-period-color, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82))));
        --mat-datepicker-calendar-period-button-icon-color: var(--picker-text-primary-color, var(--picker-calendar-period-color, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82))));
        --mat-datepicker-calendar-text-size: 14px;
        --mat-datepicker-calendar-text-font: var(--filter-font-family, var(--chart-editor-font-family));
        --mat-datepicker-calendar-body-label-text-color: var(--picker-text-primary-color, ${ink82});
        --mat-datepicker-calendar-header-text-color: ${ink54};
        --mat-datepicker-calendar-container-text-color: var(--picker-text-primary-color, ${ink82});
        --mat-datepicker-calendar-date-text-color: var(--picker-text-primary-color, ${ink82});
        --mat-datepicker-calendar-navigation-button-icon-color: var(--picker-text-primary-color, ${ink82});
        --mat-datepicker-calendar-date-selected-state-text-color: #fff;
        --mat-datepicker-calendar-date-selected-state-background-color: var(--picker-accent-color, var(--filter-accent-color, #651fff));
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-cell:not(.mat-calendar-body-disabled) .mat-calendar-body-cell-content:not(.mat-calendar-body-selected) {
        color: var(--picker-text-primary-color, var(--Jepto---Light-text-primary, rgba(15, 21, 38, 0.82))) !important;
      }

      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-disabled .mat-calendar-body-cell-content {
        color: var(--picker-ink-54, ${ink54}) !important;
      }

      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-table-header th,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-table-header .mat-calendar-body-cell-content {
        color: var(--picker-ink-54, ${ink54}) !important;
      }

      ${buildDualPanelCalendarHighlightStyles({
			selectorPrefix:
				'.cdk-overlay-pane:has(.custom-ckd-container) .picker-panel',
			accentColor,
			accentColorLight,
		})}

      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-preview-start .mat-calendar-body-cell-preview,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-preview-end .mat-calendar-body-cell-preview,
      .cdk-overlay-pane:has(.custom-ckd-container) .mat-calendar-body-in-preview .mat-calendar-body-cell-preview {
        background: ${accentColorLight} !important;
      }

      .cdk-overlay-pane:has(.custom-ckd-container) .d-inline.buttons button.mat-primary {
        background: ${accentColor} !important;
      }

      .cdk-overlay-pane:has(.custom-ckd-container) .mat-mdc-action-list .mdc-list-item--activated .mdc-list-item__primary-text,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__presets .picker-panel__preset--active .mdc-list-item__primary-text {
        color: ${accentColor} !important;
        font-weight: 500;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__presets .picker-panel__preset--active {
        background: ${dark ? accentBgDark : accentBgLight} !important;
      }

      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button {
        --mdc-radio-selected-focus-icon-color: ${accentColor} !important;
        --mdc-radio-selected-hover-icon-color: ${accentColor} !important;
        --mdc-radio-selected-icon-color: ${accentColor} !important;
        --mdc-radio-selected-pressed-icon-color: ${accentColor} !important;
        --mat-radio-checked-ripple-color: ${accentColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-radio__outer-circle,
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-radio__inner-circle {
        border-color: ${accentColor} !important;
      }
      .cdk-overlay-pane:has(.custom-ckd-container) .picker-panel__dynamic-form .mat-mdc-radio-button .mdc-radio__inner-circle {
        background-color: ${accentColor} !important;
      }

      ${calendarText}
    `;
		this.styleElement.textContent = overlayCss
			.split('.cdk-overlay-pane:has(.custom-ckd-container)')
			.join(
				`.cdk-overlay-pane:has(.custom-ckd-container[data-picker-scope="${this.styleId}"])`
			);
		document.head.appendChild(this.styleElement);
	}

	private removeOverlayStyles(): void {
		if (!this.isBrowser) return;

		const existing = document.getElementById(this.styleId);
		if (existing) {
			existing.remove();
		}
		this.styleElement = null;
	}

	/** Resolves selection from external input, internal commit, or preset fallback. */
	private resolveActiveExpression(): DateRangeExpression | null {
		const inputExpression = parseDateRangeExpressionInput(this.dateRange());
		if (this.pendingCommitExpression) {
			if (
				inputExpression &&
				dateRangeExpressionsEqual(
					inputExpression,
					this.pendingCommitExpression
				)
			) {
				this.pendingCommitExpression = null;
			} else {
				return this.pendingCommitExpression;
			}
		}
		if (inputExpression) {
			this.isDateRangeControlled.set(true);
			return inputExpression;
		}
		if (this.isDateRangeControlled()) {
			this.committedExpression.set(null);
			return null;
		}
		return this.committedExpression();
	}

	#resolveClosedDisplayDateRange(): DateRange<Date> | null {
		const expression =
			parseDateRangeExpressionInput(this.dateRange()) ??
			this.committedExpression();
		if (expression) {
			const resolved = tryResolveDateRangeExpression(
				expression,
				this.resolveOptions()
			);
			if (resolved?.startDate && resolved?.endDate) {
				return new DateRange(resolved.startDate, resolved.endDate);
			}
		}
		if (this.selectedDateRange?.start && this.selectedDateRange?.end) {
			return this.selectedDateRange;
		}
		return null;
	}

	/** Syncs internal selection from `dateRange` input or last Done apply (not on every panel close). */
	private syncSelectionFromSources(): void {
		const expression = this.resolveActiveExpression();
		if (expression) {
			const resolved = tryResolveDateRangeExpression(
				expression,
				this.resolveOptions()
			);
			if (!resolved) {
				this.selectedDateRange = this.getLastMonthDateRange();
				this.activePresetId.set(null);
				this.tempSelectedOption.set(null);
				this.tempExpression.set(null);
				return;
			}
			this.selectedDateRange = new DateRange(
				resolved.startDate,
				resolved.endDate
			);

			if (
				this.syncFilterService() &&
				this.selectedDateRange.start &&
				this.selectedDateRange.end
			) {
				const startDate = this.formatDateToLocalISO(
					this.selectedDateRange.start
				);
				const endDate = this.formatDateToLocalISO(
					this.selectedDateRange.end
				);
				this.filterService.setDateRangeWithExpression(
					{ startDate, endDate },
					expression
				);
			}

			if (expression.length === 1) {
				const presetId = expression[0];
				const preset = this.presetOptions().find(
					p => p.id === presetId
				);
				if (preset) {
					this.activePresetId.set(preset.id);
					this.tempSelectedOption.set(this.optionFromPreset(preset));
				}
			} else if (
				this.selectedDateRange?.start &&
				this.selectedDateRange?.end
			) {
				const isDynamic = isDynamicRangeExpression(expression);
				if (isDynamic) {
					this.activePresetId.set(null);
					this.tempExpression.set(expression);
					this.tempSelectedOption.set({
						id: 'dynamic',
						label: labelForDateRangeExpression(
							expression,
							this.resolveOptions()
						),
						startDate: this.selectedDateRange.start,
						endDate: this.selectedDateRange.end,
						isSelected: true,
					});
				} else {
					const expressionPreset = this.findPresetMatchingExpression(
						this.presetOptions(),
						expression
					);
					if (expressionPreset) {
						this.activePresetId.set(expressionPreset.id);
						this.tempSelectedOption.set(
							this.optionFromPreset(expressionPreset)
						);
						this.tempExpression.set(expression);
						return;
					}
					const matchingPreset = this.findPresetMatchingRange(
						this.presetOptions(),
						this.selectedDateRange
					);
					if (matchingPreset) {
						this.activePresetId.set(matchingPreset.id);
						this.tempSelectedOption.set(
							this.optionFromPreset(matchingPreset)
						);
					} else {
						this.activePresetId.set('custom');
						this.tempSelectedOption.set(null);
					}
					this.tempExpression.set(expression);
				}
			}
		} else {
			const presetId = this.selectedPresetId();
			const preset = presetId
				? this.presetOptions().find(p => p.id === presetId)
				: null;
			if (preset && preset.id !== 'custom') {
				const range = this.resolvePresetRange(preset);
				this.selectedDateRange = range;
				this.activePresetId.set(preset.id);
				this.tempSelectedOption.set(this.optionFromPreset(preset));
				this.tempExpression.set(preset.expression);
				if (this.syncFilterService() && range?.start && range?.end) {
					const startDate = this.formatDateToLocalISO(range.start);
					const endDate = this.formatDateToLocalISO(range.end);
					this.filterService.setDateRangeWithExpression(
						{ startDate, endDate },
						preset.expression
					);
				}
			} else {
				this.selectedDateRange = this.getLastMonthDateRange();
				if (
					this.syncFilterService() &&
					this.selectedDateRange.start &&
					this.selectedDateRange.end
				) {
					const startDate = this.formatDateToLocalISO(
						this.selectedDateRange.start
					);
					const endDate = this.formatDateToLocalISO(
						this.selectedDateRange.end
					);
					this.filterService.setDateRange({ startDate, endDate });
				}
			}
		}
	}

	/** Gate for open(), and any future keyboard/focus handlers that launch the panel. */
	#isPanelInteractionBlocked(): boolean {
		return this.disabled() || this.displayOnly();
	}

	open(): void {
		if (this.#isPanelInteractionBlocked()) {
			return;
		}
		const dates = this.selectedDateRange;
		const presets = this.presetOptions();
		const range =
			dates?.start && dates?.end
				? new DateRange(dates.start, dates.end)
				: null;
		const existingDynamic = this.tempExpression();
		const restoredExpression =
			this.resolveActiveExpression() ??
			(existingDynamic && isDynamicRangeExpression(existingDynamic)
				? existingDynamic
				: null);

		this.tempDateRange.set(range);
		this.activeView.set('presets');

		let matchingPreset = range
			? this.findPresetMatchingRange(presets, range)
			: null;
		if (!matchingPreset && range) {
			const fallbackPresetId = this.selectedPresetId();
			const fallbackPreset = fallbackPresetId
				? presets.find(p => p.id === fallbackPresetId)
				: null;
			if (
				fallbackPreset &&
				fallbackPreset.id !== 'custom' &&
				this.rangeMatchesPresetByCalendarDay(range, fallbackPreset)
			) {
				matchingPreset = fallbackPreset;
			}
		}

		if (matchingPreset) {
			this.activePresetId.set(matchingPreset.id);
			this.tempDateRange.set(this.resolvePresetRange(matchingPreset));
			this.tempSelectedOption.set(this.optionFromPreset(matchingPreset));
			this.tempExpression.set(matchingPreset.expression);
		} else if (
			restoredExpression &&
			isDynamicRangeExpression(restoredExpression) &&
			range
		) {
			this.activePresetId.set(null);
			this.tempExpression.set(restoredExpression);
			this.tempSelectedOption.set({
				id: 'dynamic',
				label: labelForDateRangeExpression(
					restoredExpression,
					this.resolveOptions()
				),
				startDate: range.start,
				endDate: range.end,
				isSelected: true,
			});
		} else if (range) {
			this.applyCustomCalendarSelectionState(
				range,
				restoredExpression &&
					!isDynamicRangeExpression(restoredExpression)
					? restoredExpression
					: null
			);
		} else {
			const fallbackPresetId = this.selectedPresetId();
			const preset = fallbackPresetId
				? presets.find(p => p.id === fallbackPresetId)
				: null;
			if (preset) {
				this.activePresetId.set(preset.id);
				if (preset.id !== 'custom') {
					this.tempDateRange.set(this.resolvePresetRange(preset));
					this.tempSelectedOption.set(this.optionFromPreset(preset));
					this.tempExpression.set(preset.expression);
				} else {
					this.tempSelectedOption.set(null);
				}
			} else {
				this.activePresetId.set(null);
				this.tempSelectedOption.set(null);
			}
		}

		this.isOpen.set(true);
		this.tempWeekStart.set(this.appliedWeekStart());
		this.syncCalendarMonthsFromRange(this.calendarDisplayRange());
		this.bumpCalendarRevision();
		// Re-apply overlay styles with current theme when panel opens (ensures dark mode is correct)
		this.updateOverlayStyles();
	}

	close(): void {
		this.isOpen.set(false);
		this.tempWeekStart.set(this.appliedWeekStart());
		this.activeView.set('presets');
		this.tempDateRange.set(null);
	}

	onPanelWeekStartChange(value: WeekStartSetting): void {
		const weekStart = coerceWeekStart(value);
		if (this.tempWeekStart() === weekStart) {
			return;
		}
		this.tempWeekStart.set(weekStart);
		this.applyWeekStartToCalendarAdapter(weekStart);
		this.refreshTempRangeForWeekStart();
		this.bumpCalendarRevision();
	}

	private assertPanelHost(): JeptoDateRangePickerPanelDirective {
		const host = this.panelHost();
		if (!host) {
			throw new Error('Missing jeptoDateRangePickerPanel directive');
		}
		return host;
	}

	private applyWeekStartToCalendarAdapter(weekStart: WeekStart): void {
		const firstDay = getFirstDayOfWeekIndex(weekStart);
		this.assertPanelHost().weekStartConfig.setFirstDayOfWeek(firstDay);
	}

	private bumpCalendarRevision(): void {
		this.calendarRevision.update(revision => revision + 1);
	}

	private startOfDayLocal(date: Date): Date {
		const normalized = new Date(date);
		normalized.setHours(0, 0, 0, 0);
		return normalized;
	}

	/**
	 * Left panel = start month; right panel = end month (e.g. Last Year → Jan + Dec).
	 * Same-month ranges show that month on both panels so start and end are visible.
	 */
	private syncCalendarMonthsFromRange(range: DateRange<Date> | null): void {
		if (!range?.start || !range?.end) {
			return;
		}

		const start = this.startOfDayLocal(range.start);
		const end = this.startOfDayLocal(range.end);
		const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
		const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

		this.leftMonth.set(startMonth);
		this.rightMonth.set(endMonth);
	}

	/**
	 * MatCalendar only reads `[startAt]` once in `ngAfterContentInit`. When the same
	 * calendar instance is reused, force `activeDate` so presets like Last Year open
	 * on the range start/end months with visible selection.
	 */
	private applyCalendarActiveDates(): void {
		if (!this.isOpen()) {
			return;
		}
		const calendars = this.matCalendars();
		if (!calendars.length) {
			return;
		}
		const left = this.leftMonth();
		const right = this.rightMonth();
		calendars[0].activeDate = left;
		if (calendars[1]) {
			calendars[1].activeDate = right;
		}
	}

	private applyDualPanelCalendarA11y(): void {
		if (!this.isOpen() || !this.isBrowser) {
			return;
		}
		const panel = this.panelHost()?.hostElement.nativeElement;
		if (!panel) {
			return;
		}
		applyDualPanelCalendarA11y(panel);
	}

	private refreshTempRangeForWeekStart(): void {
		const presetId = this.activePresetId();
		const preset = presetId
			? this.presetOptions().find(p => p.id === presetId)
			: null;

		if (preset && preset.id !== 'custom') {
			this.tempDateRange.set(this.resolvePresetRange(preset));
			this.tempSelectedOption.set(this.optionFromPreset(preset));
			this.tempExpression.set(preset.expression);
			return;
		}

		const expr = this.tempExpression();
		if (!expr) {
			return;
		}

		const resolved = tryResolveDateRangeExpression(
			expr,
			this.resolveOptions()
		);
		if (resolved) {
			this.tempDateRange.set(
				new DateRange(resolved.startDate, resolved.endDate)
			);
		}
	}

	private commitPanelWeekStart(): void {
		const next = this.tempWeekStart();
		const previous = this.appliedWeekStart();
		this.appliedWeekStart.set(next);
		if (next !== previous) {
			this.weekStartChange.emit(next);
		}
	}

	selectPreset(preset: DateRangePresetOption): void {
		this.activePresetId.set(preset.id);
		if (preset.id === 'custom') {
			this.activeView.set('calendar');
			this.tempDateRange.set(this.resolvePresetRange(preset));
			this.tempSelectedOption.set(null);
			this.tempExpression.set(preset.expression);
		} else {
			this.tempDateRange.set(this.resolvePresetRange(preset));
			this.tempSelectedOption.set(this.optionFromPreset(preset));
			this.tempExpression.set(preset.expression);
			this.activeView.set('presets');
		}
		this.syncCalendarMonthsFromRange(this.calendarDisplayRange());
		this.bumpCalendarRevision();
	}

	showDynamicForm(): void {
		this.activePresetId.set(null);
		this.activeView.set('dynamic');
		this.bumpCalendarRevision();
	}

	apply(): void {
		if (this.activeView() === 'dynamic') {
			this.applyDynamicConfig();
			return;
		}
		const expressionFromTemp = this.tempExpression();
		const range = this.tempDateRange();
		if (range?.start && range?.end) {
			const finalExpression =
				expressionFromTemp ??
				expressionFromResolvedDates(
					range.start,
					range.end,
					this.resolveOptions()
				);
			this.pendingCommitExpression = finalExpression;
			this.committedExpression.set(finalExpression);
			this.selectedDateRange = range;
			const matchingPreset = this.findPresetMatchingRange(
				this.presetOptions(),
				range
			);
			if (matchingPreset) {
				this.activePresetId.set(matchingPreset.id);
			} else if (!isDynamicRangeExpression(finalExpression)) {
				this.activePresetId.set('custom');
			}
			const startDate = this.formatDateToLocalISO(range.start);
			const endDate = this.formatDateToLocalISO(range.end);
			if (this.syncFilterService()) {
				this.filterService.setDateRangeWithExpression(
					{ startDate, endDate },
					finalExpression
				);
			}
			this.dateRangeChange.emit(finalExpression);
		}
		this.commitPanelWeekStart();
		this.close();
	}

	private applyDynamicConfig(): void {
		if (this.dynamicRangeValidationError()) return;
		const expression = this.dynamicPreviewExpression();
		const resolved = resolveDateRangeExpression(
			expression,
			this.resolveOptions()
		);
		const range = new DateRange(resolved.startDate, resolved.endDate);
		this.tempDateRange.set(range);
		this.tempExpression.set(expression);
		this.pendingCommitExpression = expression;
		this.committedExpression.set(expression);
		this.selectedDateRange = range;
		this.tempSelectedOption.set({
			id: 'dynamic',
			label: labelForDateRangeExpression(
				expression,
				this.resolveOptions()
			),
			startDate: resolved.startDate,
			endDate: resolved.endDate,
			isSelected: true,
		});

		const startDate = this.formatDateToLocalISO(resolved.startDate);
		const endDate = this.formatDateToLocalISO(resolved.endDate);
		if (this.syncFilterService()) {
			this.filterService.setDateRangeWithExpression(
				{ startDate, endDate },
				expression
			);
		}
		this.dateRangeChange.emit(expression);
		this.commitPanelWeekStart();
		this.close();
	}

	onCalendarDateSelected(date: Date): void {
		const switchingFromPreset = this.shouldSwitchToCustomOnCalendarPick();
		const current = this.tempDateRange();
		const start = current?.start ?? null;
		const end = current?.end ?? null;
		const newRange =
			switchingFromPreset ||
			!current ||
			(start && end) ||
			(start && date && start > date)
				? new DateRange<Date>(date, null)
				: new DateRange<Date>(start, date);
		this.tempDateRange.set(newRange);

		if (switchingFromPreset) {
			this.activePresetId.set('custom');
			this.tempSelectedOption.set(null);
			this.tempExpression.set(null);
		} else if (this.activePresetId() === 'custom') {
			this.tempExpression.set(null);
		}

		this.syncCalendarMonthsFromRange(this.calendarDisplayRange());
		this.bumpCalendarRevision();
	}

	/** Sidebar presets (yesterday … this year) — manual calendar picks become Custom range. */
	private shouldSwitchToCustomOnCalendarPick(): boolean {
		const presetId = this.activePresetId();
		if (
			!presetId ||
			presetId === 'custom' ||
			this.activeView() === 'dynamic'
		) {
			return false;
		}
		return this.presetOptions().some(
			preset => preset.id === presetId && preset.id !== 'custom'
		);
	}

	onOverlayOutsideClick(event: MouseEvent): void {
		const target = event?.target as HTMLElement | null;
		if (target?.closest?.('.mat-datepicker-content')) {
			return;
		}
		this.close();
	}

	getDynamicUnitLabel(unit: DateRangeUnit, count: number): string {
		const option = this.unitOptions.find(o => o.value === unit);
		if (!option) return unit;
		return Math.abs(count) === 1 ? option.label : option.pluralLabel;
	}

	private findPresetMatchingExpression(
		presets: DateRangePresetOption[],
		expression: DateRangeExpression
	): DateRangePresetOption | null {
		return (
			presets.find(
				preset =>
					preset.id !== 'custom' &&
					dateRangeExpressionsEqual(preset.expression, expression)
			) ?? null
		);
	}

	private findPresetMatchingRange(
		presets: DateRangePresetOption[],
		range: DateRange<Date>
	): DateRangePresetOption | null {
		if (!range.start || !range.end) return null;
		for (const preset of presets) {
			if (preset.id === 'custom') continue;
			const presetRange = this.resolvePresetRange(preset);
			if (
				presetRange.start &&
				presetRange.end &&
				this.sameCalendarDay(presetRange.start, range.start) &&
				this.sameCalendarDay(presetRange.end, range.end)
			) {
				return preset;
			}
		}
		return null;
	}

	private sameCalendarDay(a: Date, b: Date): boolean {
		return (
			a.getFullYear() === b.getFullYear() &&
			a.getMonth() === b.getMonth() &&
			a.getDate() === b.getDate()
		);
	}

	private rangeMatchesPresetByCalendarDay(
		range: DateRange<Date>,
		preset: DateRangePresetOption
	): boolean {
		if (!range.start || !range.end || preset.id === 'custom') return false;
		const presetRange = this.resolvePresetRange(preset);
		if (!presetRange.start || !presetRange.end) return false;
		return (
			this.sameCalendarDay(range.start, presetRange.start) &&
			this.sameCalendarDay(range.end, presetRange.end)
		);
	}

	private applyCustomCalendarSelectionState(
		range: DateRange<Date>,
		expression: DateRangeExpression | null
	): void {
		this.activePresetId.set('custom');
		this.tempDateRange.set(range);
		this.tempExpression.set(expression);
		this.tempSelectedOption.set(null);
	}

	private getFixedStartDate(): Date {
		const d = this.fixedStartDate();
		if (d) {
			const out = new Date(d);
			out.setHours(0, 0, 0, 0);
			return out;
		}
		const defaultStart = new Date();
		defaultStart.setDate(defaultStart.getDate() - 30);
		defaultStart.setHours(0, 0, 0, 0);
		return defaultStart;
	}

	private getRelativeEndDate(): Date {
		if (this.endIsToday()) {
			return resolveDateRangeExpression(['today'], this.resolveOptions())
				.endDate;
		}
		const endToken = buildRelativeAgoToken(
			this.endOffset(),
			this.endUnit()
		);
		return resolveDateRangeExpression(
			[endToken, endToken],
			this.resolveOptions()
		).endDate;
	}

	private buildDynamicLabel(): string {
		if (this.startType() === 'fixed') {
			const start = this.getFixedStartDate();
			const startStr = formatDateToISO(start);
			if (this.endIsToday()) {
				return `${startStr} to Today`;
			}
			const endOff = Math.abs(this.endOffset());
			const endUnitLabel = this.getDynamicUnitLabel(
				this.endUnit(),
				endOff
			);
			return `${startStr} to ${endOff} ${endUnitLabel} ago`;
		}
		const startOff = Math.abs(this.startOffset());
		const startUnitLabel = this.getDynamicUnitLabel(
			this.startUnit(),
			startOff
		);
		if (this.endIsToday()) {
			return `Last ${startOff} ${startUnitLabel}`;
		}
		const endOff = Math.abs(this.endOffset());
		const endUnitLabel = this.getDynamicUnitLabel(this.endUnit(), endOff);
		return `${startOff} ${startUnitLabel} ago to ${endOff} ${endUnitLabel} ago`;
	}

	private resolvePresetRange(preset: DateRangePresetOption): DateRange<Date> {
		return toMaterialDateRangeFromExpression(
			preset.expression,
			this.resolveOptions()
		);
	}

	private optionFromPreset(preset: DateRangePresetOption): ISelectDateOption {
		const range = this.resolvePresetRange(preset);
		return {
			id: preset.id,
			label: preset.label,
			startDate: range.start!,
			endDate: range.end!,
			isSelected: true,
		};
	}

	getLastMonthDateRange(): DateRange<Date> {
		const now = new Date();
		const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
		return new DateRange(startDate, endDate);
	}

	private formatDateToLocalISO(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private hexToRgba(hex: string, alpha: number): string {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		if (!result) return hex;
		const r = parseInt(result[1], 16);
		const g = parseInt(result[2], 16);
		const b = parseInt(result[3], 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}
}

function relativeUnitToFormUnit(
	unit: 'days' | 'weeks' | 'months' | 'quarters' | 'years'
): DateRangeUnit {
	switch (unit) {
		case 'days':
			return 'day';
		case 'weeks':
			return 'week';
		case 'months':
			return 'month';
		case 'quarters':
			return 'quarter';
		case 'years':
			return 'year';
	}
}
